/**
 * 類似日検索ダイアログ。
 *
 * 候補日リスト（日付/曜日3区分/日区分4区分/天気/最高気温/最低気温/露点/最大需要/最小需要/チェックボックス）を表示し、
 * ユーザーが選択した日付群を「適用」で呼び出し元に渡す。
 *
 * 選択上限: MAX_SELECTABLE = 5 (AC-021/022: 上限5件)
 * 「自動選択」: auto_selected=true の候補を全選択（上限 MAX_SELECTABLE 件）
 * 「任意日追加」(AC-DFD-022): カレンダー（日付ピッカー）で選んだ日を選択リストに追加する。
 *   日付文字列のキーボード手打ちは不可（`shared-components.md` §8）。
 *   ※ 同 §8 の祝日・特異日マーカー (AC-SC-027/028) は未対応。 DS `Calendar` に
 *   日セルを装飾する口が無く props では実現できないため (#4845)。 ホバーで出す
 *   祝日名も、祝日データの期間取得が未配線のため出さない (#13081)。
 * 「適用」: 1件以上選択時のみ活性。onApply(selectedDates) を呼ぶ
 *
 * 選択上限到達時 (AC-06): 未選択行を disabled にし、「上限到達」警告を表示する。
 *
 * 遡る日数: lookbackDays (7-365, 既定30)。変更時に onLookbackDaysChange を呼ぶ。
 * AC-021 上限警告: lookbackDays > 90 の時に警告文を表示する。
 *
 * 曜日セル (AC-03/AC-04): 曜日 1 文字 + 色トークン意味値 + 「祝」バッジは
 * `similarDayWeekdayCell` (models/similarDayDialog.ts) に委譲する。
 * 天気セル (AC-01/AC-02): 絵文字 + tooltip ラベルは `models/weather/telop.ts` の
 * `weatherCategoryEmojiOf` / `weatherCategoryLabelOf` に委譲する。
 *
 * 祝日名 (AC-016): 「祝」バッジの隣に併記する。holidayName が空文字の場合は省略 (graceful)。
 * 祝日名の表示は weekdayCategory に依存しない (`similarDayWeekdayCell` 参照)。
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import type { MessageShape } from '@bufbuild/protobuf'
import {
  DayType,
  type SimilarDayCandidateSchema,
  WeatherCategory,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Checkbox,
  Flex,
  Input,
  LightModeSemanticColors,
  SecondaryButton,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  TertiaryButton,
  Text,
  Tooltip,
  VStack,
  fontSizes,
} from '@enechain/ecloud-designsystem'
import { FaMagnifyingGlassRegular } from '@enechain/ecloud-icons'

import { ActionReasonText } from '~/components/common/ActionReasonText'
import { Badge } from '~/components/common/Badge'
import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { EmptyPanel } from '~/components/common/EmptyPanel'
import { FieldLabel, FieldRoot } from '~/components/common/Field'
import { HintTooltip } from '~/components/common/HintTooltip'
import { TABLE_HEADER_BG } from '~/components/common/MasterDataTable'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { RegionLoading } from '~/components/common/RegionLoading'
import { iconSize } from '~/constants/iconSize'
import {
  type CivilDate,
  civilDateToInputValue,
  isValidCivilDate,
} from '~/models/civilDate'
import {
  weatherCategoryEmojiOf,
  weatherCategoryLabelOf,
} from '~/models/weather/telop'
import { dayTypeBadgeStatus } from '~/pages/DemandForecastDaily/components/SectionHeader'
import type { SimilarDayWeekdayCell } from '~/pages/DemandForecastDaily/models/similarDayDialog'
import {
  selectedDatesNotInCandidates,
  similarDayWeekdayCell,
  toggleDaySelection,
} from '~/pages/DemandForecastDaily/models/similarDayDialog'

const MAX_SELECTABLE = 5
const LOOKBACK_DAYS_MIN = 7
const LOOKBACK_DAYS_MAX = 365
const LOOKBACK_DAYS_DEFAULT = 30
/** この値を超えると上限警告を表示する (AC-021) */
const LOOKBACK_DAYS_WARNING_THRESHOLD = 90

/**
 * 候補日テーブルの `.ecl-table` 準拠スタイル (design 実測、
 * `design/css/components.css:776-808`)。 TH 背景色は新しい定数を作らず
 * `MasterDataTable` の `TABLE_HEADER_BG` (single source) を import する。
 */
const TABLE_BORDER_COLOR = LightModeSemanticColors.Border.lowEmphasis.value
const similarDayThStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  background: TABLE_HEADER_BG,
  padding: '10px 12px',
  fontWeight: 600,
  fontSize: fontSizes.md.value,
  color: LightModeSemanticColors.Text.midEmphasis.value,
  borderBottom: `1px solid ${TABLE_BORDER_COLOR}`,
  zIndex: 1,
  whiteSpace: 'nowrap',
}
/** 数値列 (気温・露点・需要) は右寄せ + tabular-nums (design `.ecl-table th.num`) */
const similarDayThNumStyle: React.CSSProperties = {
  ...similarDayThStyle,
  textAlign: 'right',
}
const similarDayTdStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderBottom: `1px solid ${TABLE_BORDER_COLOR}`,
  whiteSpace: 'nowrap',
  fontSize: fontSizes.md.value,
}
/** 数値列 (design `.ecl-table td.num`) */
const similarDayTdNumStyle: React.CSSProperties = {
  ...similarDayTdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
}
/**
 * 曜日・天気の列は中央寄せ (UI 辞書 similar-day-search.md `.sd-cell-dow` /
 * `.sd-cell-weather`)。 TH も同じ揃え。
 */
const similarDayThCenterStyle: React.CSSProperties = {
  ...similarDayThStyle,
  textAlign: 'center',
}
const similarDayTdCenterStyle: React.CSSProperties = {
  ...similarDayTdStyle,
  textAlign: 'center',
}
/** 選択のチェック列は幅 48px・中央寄せ (同 `.sd-col-check`)。 */
const similarDayThCheckStyle: React.CSSProperties = {
  ...similarDayThCenterStyle,
  width: '48px',
}
const similarDayTdCheckStyle: React.CSSProperties = {
  ...similarDayTdCenterStyle,
  width: '48px',
}
/**
 * 観測気象が無い日の「—」(spec AC-DFD-017a)。 値ではなく「まだ無い」ことを示すので
 * low-emphasis・中央寄せ (同 `.sd-cell-noobs`)。
 */
const similarDayTdNoObsStyle: React.CSSProperties = {
  ...similarDayTdCenterStyle,
  color: LightModeSemanticColors.Text.lowEmphasis.value,
}

type Candidate = MessageShape<typeof SimilarDayCandidateSchema>

type Props = {
  /** ダイアログの開閉状態 */
  isOpen: boolean
  /** 候補日リスト (SearchSimilarDays レスポンス) */
  candidates: Candidate[]
  /** SearchSimilarDays 読込中 */
  isLoading: boolean
  /**
   * SearchSimilarDays の取得に失敗した (issue #10429)。 `isLoading` とは排他
   * (再試行中は flow が `isLoading` だけを立てる) で、 本 dialog は組み合わせを確かめ直さない。
   */
  isError: boolean
  /** 取得失敗時の「再試行」 */
  onRetry: () => void
  /** 初期選択済み日付 (YYYY-MM-DD 形式) */
  initialSelected: string[]
  /** 「適用」クリック (selectedDates: YYYY-MM-DD[]) */
  onApply: (selectedDates: string[]) => void
  /** ダイアログを閉じる */
  onCancel: () => void
  /**
   * 遡る日数変更時のコールバック。変更されたら親が useSearchSimilarDays を再実行する。
   * 指定しない場合は lookbackDays 入力を表示しない。
   */
  onLookbackDaysChange?: (days: number) => void
  /** 現在の遡る日数 (未指定時は LOOKBACK_DAYS_DEFAULT) */
  lookbackDays?: number
  /**
   * 自動選択が構造的に成立しない断面での理由文言 (i18n 済み)。 渡された時だけ
   * 「自動選択」を非活性にし、 この文言を tooltip で読ませる (issue #6720)。
   *
   * spec `demand-forecast-daily.md` は「翌々日断面での『自動選択』ボタンの表示・
   * 非活性・案内表示は Des 判断とする」と委ねており、 8080 プロトの判断
   * (`demand/forecast-today.html` の `AUTO_SELECT_NA_REASON`) に従って
   * **非表示にせず非活性 + 理由表示**とする — 非表示だと「機能が無い」のか
   * 「今使えない」のかが区別できず、 断面を変えれば使えるという回復手段も
   * 学習できないため。
   *
   * 理由を判定できるのは断面を知るホスト画面側なので、 文言は呼び出し元が渡す。
   */
  autoSelectUnavailableReason?: string
}

/** google.type.Date → YYYY-MM-DD 文字列 */
function protoDateToStr(date: Candidate['date']): string | undefined {
  if (date === undefined) return undefined
  const y = date.year.toString().padStart(4, '0')
  const m = date.month.toString().padStart(2, '0')
  const d = date.day.toString().padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** YYYY-MM-DD → yyyy/MM/dd 形式（表示用、spec AC-014/design doc AC-19） */
function formatDateLabel(dateStr: string): string {
  const [y, m, d] = dateStr.split('-')
  return `${y ?? ''}/${m ?? ''}/${d ?? ''}`
}

/** 「日付を手動追加」欄の native `<input type="date">` の id (label / test 入口)。 */
const MANUAL_DATE_FIELD_ID = 'similar-day-manual-date'
/** 「遡る日数」入力の id ([[FieldLabel]] の htmlFor 用)。 */
const LOOKBACK_FIELD_ID = 'similar-day-lookback-days'

/** colorToken 意味値 → Text color prop (design token 文字列、AC-03)。 */
const WEEKDAY_COLOR_TOKEN: Record<SimilarDayWeekdayCell['colorToken'], string> =
  {
    default: 'Text.midEmphasis',
    info: 'Text.link',
    negative: 'Text.negativeMid',
  }

/**
 * 類似日検索ダイアログ。
 */
export const SimilarDayDialog = ({
  isOpen,
  candidates,
  isLoading,
  isError,
  onRetry,
  initialSelected,
  onApply,
  onCancel,
  onLookbackDaysChange,
  lookbackDays = LOOKBACK_DAYS_DEFAULT,
  autoSelectUnavailableReason,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const [selectedDates, setSelectedDates] = useState<string[]>(initialSelected)
  const [manualDate, setManualDate] = useState<CivilDate | undefined>(undefined)
  const [lookbackInput, setLookbackInput] = useState<string>(
    String(lookbackDays),
  )

  /**
   * 「自動選択」を押したが 1 件も選べなかった時に出す案内 (issue #6720)。
   *
   * 押しても何も起きないと、 担当者からは「壊れている」 のか「条件に合う日が
   * 無い」 のか区別できない。 原因は response からは判別できない (proto
   * `SearchSimilarDaysResponse` は理由を持たず、 候補の `auto_selected` フラグしか
   * 無い) ので、 **起きた事実と回復手段だけを書き、 原因を断定しない**。
   */
  const [autoSelectEmpty, setAutoSelectEmpty] = useState(false)

  /** 「日付を手動追加」 欄の DOM (空状態のボタンからフォーカスを運ぶ先)。 */
  const manualDateRef = useRef<HTMLDivElement>(null)

  const showLookbackWarning = lookbackDays > LOOKBACK_DAYS_WARNING_THRESHOLD
  /** AC-06: 選択上限到達時に警告文を表示する */
  const isAtSelectionLimit = selectedDates.length >= MAX_SELECTABLE

  /**
   * 表に出す行 = 「候補一覧に無い選択日」 + backend 候補。
   *
   * 「日付を手動追加」 (AC-DFD-022) で足せる日には**遡る日数の窓外**も含まれる
   * (窓内なら候補一覧から選べるので、手で足す動機はそもそも窓外にある)。
   * 窓外の日は `candidates` に無いため、 `candidates` だけを描いていると
   * 「追加」を押しても行が出ず、 選択件数カウンタも無い設計なので**選べた
   * ことがどこにも出ない** (2026-09-11 デザイナー指摘「選択できているかが
   * わかりづらい」)。 design (8080) `_addManualSimilar` の「範囲に無ければ
   * 行を先頭に挿入」に揃えて、 擬似候補行を**先頭**に置く。
   *
   * 同じ理由で、 過去実績ヘッダー由来の `initialSelected` が窓外だった場合も
   * これで拾える (開いた直後にどこもチェックされていない状態が解消される)。
   */
  const rows = [
    ...selectedDatesNotInCandidates(
      selectedDates,
      candidates.map((c) => protoDateToStr(c.date)),
    ),
    ...candidates,
  ]

  /**
   * 「自動選択」が押せない理由 (i18n 済み)。 `undefined` なら押せる。
   *
   * 押せない状態を作る条件が複数あるので、 **理由の有無ではなく種類で分ける**
   * (2026-08-31 デザイナー指摘「そもそも押せない場合も理由と、何をしたら良いかを
   * 表示したい」)。 以前は断面由来 (翌々日) だけが tooltip を持ち、 候補 0 件は
   * **無言で押せないだけ**だった。
   *
   * 優先順は「断面由来 → 候補 0 件」。 翌々日はそもそも候補取得が成立しないので
   * 両方成り立つが、 ユーザーに伝えるべきは構造的な方 (断面を変えれば使える) だから。
   *
   * `isLoading` 中は理由を出さない — 読み込みが終われば解消する一時状態で、
   * Spinner が既にそれを語っている (ホバーしないと読めない文言を足す価値が薄い)。
   *
   * 取得失敗時は「候補日が無い」 とは言えないので、 失敗の案内を理由にする。
   */
  const autoSelectDisabledReason =
    autoSelectUnavailableReason ??
    (isError
      ? t('page:demandForecastDaily.similarDay.fetchFailed')
      : !isLoading && candidates.length === 0
        ? t('page:demandForecastDaily.similarDay.autoSelectNoCandidates')
        : undefined)

  // ユーザーが選択を変えた後は、 再取得 (失敗後の「再試行」 の成功など) で
  // `initialSelected` が新しい配列になっても初期選択で上書きしない。 閉じたら
  // 編集を捨て、 最新の初期選択へ戻す (キャンセルした手動追加が開き直しで残らない)。
  const hasEditedSelectionRef = useRef(false)
  const editSelection = (next: string[]): void => {
    hasEditedSelectionRef.current = true
    setSelectedDates(next)
  }
  useEffect(() => {
    if (!isOpen) hasEditedSelectionRef.current = false
    if (hasEditedSelectionRef.current) return
    setSelectedDates(initialSelected)
  }, [initialSelected, isOpen])

  // 本 dialog は閉じても unmount されず `isOpen` が切り替わるだけなので、 案内を
  // `initialSelected` の変化にだけ紐付けると、 **候補も初期選択も変わらないまま
  // 閉じて開き直した時に前回の失敗案内が操作前から出たまま**になる
  // (review 指摘 #6823)。 開閉の境界で必ず消す。
  useEffect(() => {
    setAutoSelectEmpty(false)
  }, [isOpen])

  const handleAutoSelect = (): void => {
    const autoSelected = candidates
      .filter((c) => c.autoSelected)
      .map((c) => protoDateToStr(c.date))
      .filter((d): d is string => d !== undefined)
      .slice(0, MAX_SELECTABLE)
    // 0 件で `setSelectedDates([])` すると、 **押す前に選んでいた日まで消える**。
    // 「押しても何も起きない」 より悪く、 issue #6720 の実害はここも含む。
    // 選択は触らず、 何が起きたかだけを出す。
    if (autoSelected.length === 0) {
      setAutoSelectEmpty(true)
      return
    }
    setAutoSelectEmpty(false)
    editSelection(autoSelected)
  }

  /**
   * 日付を**選んだ時点で**選択リストへ入れる。
   *
   * **[design 差異] 8080 (`#dft-similar-manual-add`) は「追加」ボタンを持つが、
   * 実装では置かない** (デザイナー指摘 2026-09-15「追加ボタンを消したい、
   * 追加ボタンクリックは逆に不要。 日付を選択したら画面上に選択した日付が
   * そのまま表示されていて、 選択 checkbox を外すと解除されるので、 追加ボタンが
   * 無くてもリスクはない」)。 選ぶ → 押す の 2 手が、 結果が即座に見えて取り消しも
   * できる操作に対して過剰だったため。
   *
   * ボタンが担っていた**検証はここへ移している**。 AC-DFD-022 はカレンダー選択のみを
   * 入力経路とするが、 CompactCivilDateField は keyboard 経路として visually-hidden な
   * native `<input type="date">` を残す (a11y 上必須)。 そこから 2026-02-30 のような
   * 非実在日や入力途中の値が届きうるため、 暦日として実在するものだけを選択リストへ
   * 入れる。 弾いた値は欄に残す — 黙って消えると「選んだのに何も起きない」 が
   * 「選べていない」 と区別できなくなる。
   */
  const commitManualDate = (next: CivilDate | undefined): void => {
    if (next === undefined || !isValidCivilDate(next)) {
      setManualDate(next)
      return
    }
    // 上限時は日付欄自体が disabled なので通常は到達しないが、 選択リストの
    // 不変条件 (最大 MAX_SELECTABLE 件) はここでも守る。
    if (selectedDates.length >= MAX_SELECTABLE) {
      setManualDate(next)
      return
    }
    const dateStr = civilDateToInputValue(next)
    if (!selectedDates.includes(dateStr)) {
      editSelection([...selectedDates, dateStr])
    }
    // 選択リストへ移したので欄は空へ戻す (行が出ることが「入った」の signal)。
    setManualDate(undefined)
  }

  /**
   * 空状態のボタンから「日付を手動追加」 欄へ運ぶ。
   *
   * [[CompactCivilDateField]] は imperative handle を公開していないので、
   * **この dialog が持つ ref の中だけ**を辿って anchor (読み取り専用の
   * text input) を掴み、 focus + click でカレンダーを開く。
   * `document` 全体を探さないのは、 同じ画面に別の日付欄があっても
   * 取り違えないようにするため。
   *
   * anchor は `type="date"` の visually-hidden な native input と兄弟なので、
   * `:not([type="date"])` で見分ける。
   */
  const openManualDateField = useCallback((): void => {
    const anchor = manualDateRef.current?.querySelector<HTMLInputElement>(
      'input:not([type="date"])',
    )
    anchor?.focus()
    anchor?.click()
  }, [])

  const handleToggle = (dateStr: string): void => {
    editSelection(toggleDaySelection(selectedDates, dateStr, MAX_SELECTABLE))
  }

  const handleApply = (): void => {
    onApply(selectedDates)
  }

  const handleLookbackChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setLookbackInput(e.target.value)
  }

  const handleLookbackBlur = (): void => {
    const parsed = parseInt(lookbackInput, 10)
    if (isNaN(parsed)) {
      setLookbackInput(String(lookbackDays))
      return
    }
    const clamped = Math.max(
      LOOKBACK_DAYS_MIN,
      Math.min(LOOKBACK_DAYS_MAX, parsed),
    )
    setLookbackInput(String(clamped))
    if (clamped !== lookbackDays) {
      onLookbackDaysChange?.(clamped)
    }
  }

  const footer = (
    <SaveCancelFooter
      isSaveDisabled={selectedDates.length === 0}
      onCancel={onCancel}
      onSave={handleApply}
      saveLabel={t('page:demandForecastDaily.similarDay.applyButton')}
    />
  )

  // modal 幅: `lg` (= chakra dialog `maxW: 2xl` = 672px) では候補表の右端
  // (「選択」 列) が見切れていた (2026-08-25 デザイナー指摘)。 実測 (viewport
  // 1440px): 表の自然幅 688px に対しスクロール領域は 624px しか無く 64px 欠けて
  // いた。 `xl` (= `maxW: 4xl` = 896px) なら内側に 848px 取れて収まる。
  //
  // [design 差異] 8080 (`demand/forecast-today.html` の
  // `#dft-similar-modal .ecl-modal { width: min(960px, 100%) }`) は 960px だが、
  // DS のサイズトークンに 960px は無い。 マジックナンバーで幅を直書きせず、
  // 表が収まる最小の DS トークンである `xl` を採る。
  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onCancel}
      size="xl"
      title={t('page:demandForecastDaily.similarDay.dialogTitle')}
    >
      <Flex flexDirection="column" gap={4}>
        {/* 遡る日数入力 (AC-018) + 自動選択。AC-05 (grill Q4): 「N / 5 選択中」
            カウンタと選択中日付の並列サマリ UI (旧 PastActualDateSelect) は
            過剰実装のため削除済み (候補日リストのチェックボックスのみで選択状態を表す)。 */}
        <Flex alignItems="center" flexWrap="wrap" gap={3}>
          {/* 見出しは素の `Text` ではなく DS の form label ([[FieldRoot]] +
              [[FieldLabel]]) で出す (2026-08-25 デザイナー指摘)。 label が入力と
              `htmlFor` で結びつくので `aria-label` の二重管理が要らなくなり、
              文字サイズ・色も DS の label role (14px/600/high-emphasis) に揃う。
              `orientation="horizontal"` で label を入力の左に置く。 */}
          {onLookbackDaysChange !== undefined && (
            <FieldRoot
              justifyContent="flex-start"
              gap={2}
              width="auto"
              orientation="horizontal"
            >
              <FieldLabel
                flex="0 0 auto"
                marginEnd={0}
                whiteSpace="nowrap"
                htmlFor={LOOKBACK_FIELD_ID}
              >
                {t('page:demandForecastDaily.similarDay.lookbackDays')}
              </FieldLabel>
              <Input
                id={LOOKBACK_FIELD_ID}
                max={LOOKBACK_DAYS_MAX}
                min={LOOKBACK_DAYS_MIN}
                onBlur={handleLookbackBlur}
                onChange={handleLookbackChange}
                style={{ width: '80px', textAlign: 'right' }}
                type="number"
                value={lookbackInput}
              />
              {/* 単位「日」 は design `.dft-similar-range-unit`
                  (`font-size: var(--font-size-md)` / `margin-left: -4px` /
                  mid-emphasis) 準拠。 `sm` (12px) だと入力値より 1 段小さく、
                  design より弱く見えていた。 負の margin は入力欄と単位を
                  密着させるためで、 design の実測値。 */}
              <Text
                flexShrink={0}
                marginStart="-4px"
                color="Text.midEmphasis"
                fontSize="md"
              >
                {t('page:demandForecastDaily.similarDay.lookbackDaysUnit')}
              </Text>
            </FieldRoot>
          )}
          {/* [rule 逸脱] ページ主操作ではないが size を明示しない。 design 実測ではこの行は**全要素 40px で揃っている**。
              `.ecl-input--sm` は名前に反して `height: 40px` で font-size と
              padding だけを変える modifier (`css/components.css:3302`)、
              `自動選択` `追加` も `btn-sm` が付かない素の `btn` = 40px。
              design を正として size を省略する (= DS 既定 md = 40px)。 同じ判断が Toolbar.tsx の
              「類似日から選択」 にも入っている (2026-09-11 デザイナー指摘)。

              design は「遡る日数」 群と「自動選択」 の間、 および「自動選択」 と
              「日付を手動追加」 群の間だけ 1 段広く空ける
              (`.dft-similar-controls` の `gap: var(--space-3)` に加えて各要素の
              `margin-left: var(--space-3)`)。 意味の切れ目を余白で示している。 */}
          {autoSelectDisabledReason !== undefined ? (
            <Tooltip
              content={autoSelectDisabledReason}
              placement="top"
              showArrow
            >
              {/* disabled 要素は pointer event を発火しないため span で包んで hover を
                  拾う (BlockBidTab の「ブロック作成」と同パターン)。 非表示にせず
                  非活性で残すのは `autoSelectUnavailableReason` の docstring 参照。 */}
              <span style={{ marginInlineStart: '12px' }}>
                <SecondaryButton disabled>
                  {t('page:demandForecastDaily.similarDay.autoSelect')}
                </SecondaryButton>
              </span>
            </Tooltip>
          ) : (
            /* design は `btn-secondary` (白地 + accent-primary の文字・枠線)。
               実装は `TertiaryButton` (中立グレー枠) で、同じ行の「追加」
               (design も `btn-tertiary`) と見分けが付かなくなっていた。
               この行の主アクションは「自動選択」 なので accent を持たせる。 */
            <SecondaryButton
              marginStart={3}
              disabled={isLoading}
              onClick={handleAutoSelect}
            >
              {t('page:demandForecastDaily.similarDay.autoSelect')}
            </SecondaryButton>
          )}

          {/* 任意日追加 (AC-DFD-022)。 日付の指定はカレンダー選択のみで、 日付文字列の
              手打ちはさせない (`shared-components.md` §8。 design `#dft-similar-manual`
              も flatpickr へ upgrade 済み)。 幅は flex 行なので固定する (共通の
              日付欄の幅。 design は 150px だが曜日付きの値が収まらない)。

              **design と同じく「遡る日数」「自動選択」 と同一行に置く**
              (`.dft-similar-controls` は 1 本の flex 行。 design の 2026-07-14
              の変更で「日付を手動追加」 を「自動選択」 の右へ統合済み)。
              実装は別の行として切り出していたため 2 段になっていた
              (2026-09-11 デザイナー指摘)。

              見出しは上の「遡る日数」 と同じく DS の form label ([[FieldRoot]] +
              [[FieldLabel]]) で出す (2026-08-25 デザイナー指摘)。
              [[CompactCivilDateField]] は visually-hidden な native
              `<input type="date">` を `id` 付きで持つので `htmlFor` でそこへ結びつく
              (godoc「label htmlFor 経由の accessible name」)。 `aria-label` は
              付けない — label と二重になると accessible name が競合し、 同じ文言の
              テキストノードも 2 つになる。 */}
          <FieldRoot
            justifyContent="flex-start"
            gap={2}
            width="auto"
            marginStart={3}
            whiteSpace="nowrap"
            orientation="horizontal"
          >
            <FieldLabel
              flex="0 0 auto"
              marginEnd={0}
              whiteSpace="nowrap"
              htmlFor={MANUAL_DATE_FIELD_ID}
            >
              {t('page:demandForecastDaily.similarDay.addDateLabel')}
            </FieldLabel>
            <Box
              ref={manualDateRef}
              flexShrink={0}
              width={COMPACT_DATE_FIELD_WIDTH}
            >
              <CompactCivilDateField
                disabled={isAtSelectionLimit}
                id={MANUAL_DATE_FIELD_ID}
                onChange={commitManualDate}
                value={manualDate}
              />
            </Box>
          </FieldRoot>
          {/* [design 差異] 8080 はここに「追加」 ボタン (`#dft-similar-manual-add`) を
              持つが、 実装では置かない。 日付を選んだ時点で行が出て checkbox を外せば
              解除できるため、 押す手が要らない ([[commitManualDate]] の理由を参照)。 */}
        </Flex>
        {/* AC-DFD-019・§7: 翌々日断面で「自動選択」 を押せない理由はツールチップだけに
            せず、 ボタンの行の直下に文で出す (8080 `#dft-similar-auto-reason`)。
            親の gap (16px) を 8080 の `margin-top: var(--space-1)` (4px) に詰める。 */}
        {autoSelectUnavailableReason !== undefined && (
          <Box marginTop={-3}>
            <ActionReasonText>{autoSelectUnavailableReason}</ActionReasonText>
          </Box>
        )}

        {/* issue #6720: 「自動選択」を押したが 1 件も選べなかった時の案内。
            原因 (対象日の予想気温が欠けている / 条件に合う過去日が無い) は
            response から判別できないため断定せず、 回復手段を示す。 */}
        {autoSelectEmpty && (
          <Alert
            description={t(
              'page:demandForecastDaily.similarDay.autoSelectEmpty',
            )}
            status="info"
            title=""
          />
        )}

        {/* AC-021: 遡る日数が多い場合の警告 */}
        {showLookbackWarning && (
          <Alert
            description={t(
              'page:demandForecastDaily.similarDay.lookbackDaysWarning',
            )}
            status="warning"
            title=""
          />
        )}

        {/* AC-06: 選択上限到達時の警告 (未選択行 disabled は候補リスト / 任意日追加欄側で実施) */}
        {isAtSelectionLimit && (
          <Alert
            description={t('page:demandForecastDaily.similarDay.limitReached')}
            status="warning"
            title=""
          />
        )}

        {/* 候補日リスト。 `rows` は候補一覧に無い選択日を先頭に含むので、
            候補 0 件でも手動追加した日があれば表を出す。

            **出すものが 1 つも無い時は表の枠ごと空状態 (blank UI) に差し替える**
            (2026-09-11 デザイナー指摘)。 以前は info Alert で理由だけ出していたが、
            Alert は「本来あるはずの表の上に注意書きが乗っている」 形なので、
            **そこで何をすればよいか**が伝わらない。 空状態にして、 回復手段
            そのもの (日付を手動追加) をボタンとして置く。

            原因は response から判別できない (候補の `auto_selected` フラグしか
            無い) ので断定しない (2026-08-31 デザイナー指摘)。

            手動追加した日が 1 件でもあれば表を出す — 既に行動済みなので、
            「案内は出ているのにデータもある」 という矛盾した画面にはしない。 */}
        {/* issue #10429: 候補の取得失敗はダイアログ内に出して再試行させる
            (全画面エラーにすると編集中の需要想定が消える)。 手動追加は使えるので
            入力欄は残す。 文言は spec 未記載のため暫定 (PdM が spec に追記予定)。 */}
        {isError && (
          <Alert
            description={
              <VStack align="stretch" gap={2}>
                <Text>
                  {t('page:demandForecastDaily.similarDay.fetchFailed')}
                </Text>
                <Box>
                  <TertiaryButton onClick={onRetry} size="sm">
                    {t('page:demandForecastDaily.similarDay.retry')}
                  </TertiaryButton>
                </Box>
              </VStack>
            }
            role="alert"
            status="error"
            title=""
          />
        )}

        <RegionLoading isLoading={isLoading}>
          {isLoading || (isError && rows.length === 0) ? null : rows.length >
            0 ? (
            // `maxHeight` が無いと、 `overflowY="auto"` でもこの Box は中身の高さ分だけ
            // 伸びて**自身はスクロールしない**。 その結果、 実際にスクロールするのは
            // Modal (既定 `scrollBehavior="outside"`) 側になり、 sticky なヘッダは
            // 最寄りのスクロール祖先である**この Box** を基準にするため画面外へ流れる
            // (= sticky が効かない)。 design `.similar-table-wrap` の
            // `max-height: 54vh` を移植して、 この Box 自身をスクロール領域にする。
            <Box overflowY="auto" maxHeight="54vh">
              <TableRoot
                aria-label={t(
                  'page:demandForecastDaily.similarDay.dialogTitle',
                )}
                size="sm"
                style={{ width: '100%', borderCollapse: 'collapse' }}
              >
                <TableHeader>
                  <TableRow>
                    <TableColumnHeader style={similarDayThStyle}>
                      {t('page:demandForecastDaily.similarDay.columns.date')}
                    </TableColumnHeader>
                    <TableColumnHeader style={similarDayThCenterStyle}>
                      {t(
                        'page:demandForecastDaily.similarDay.columns.dayOfWeek',
                      )}
                    </TableColumnHeader>
                    <TableColumnHeader style={similarDayThStyle}>
                      {t('page:demandForecastDaily.similarDay.columns.dayType')}
                    </TableColumnHeader>
                    <TableColumnHeader style={similarDayThCenterStyle}>
                      {t('page:demandForecastDaily.similarDay.columns.weather')}
                    </TableColumnHeader>
                    {/* 気温・露点・需要は design `.ecl-table th.num` (右寄せ) */}
                    <TableColumnHeader style={similarDayThNumStyle}>
                      {t(
                        'page:demandForecastDaily.similarDay.columns.temperature',
                      )}
                    </TableColumnHeader>
                    <TableColumnHeader style={similarDayThNumStyle}>
                      {t(
                        'page:demandForecastDaily.similarDay.columns.dewpoint',
                      )}
                    </TableColumnHeader>
                    <TableColumnHeader style={similarDayThNumStyle}>
                      {t('page:demandForecastDaily.similarDay.columns.demand')}
                    </TableColumnHeader>
                    <TableColumnHeader style={similarDayThCheckStyle}>
                      {t('page:demandForecastDaily.similarDay.columns.select')}
                    </TableColumnHeader>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((candidate, idx) => {
                    const dateStr = protoDateToStr(candidate.date)
                    if (dateStr === undefined) return null
                    const isSelected = selectedDates.includes(dateStr)
                    const isDisabled = !isSelected && isAtSelectionLimit
                    // AC-03/AC-04: 曜日 1 文字 + 色トークン意味値 + 「祝」バッジの導出。
                    const weekdayCell = similarDayWeekdayCell(
                      candidate.date,
                      candidate.weekdayCategory,
                      candidate.holidayName,
                    )
                    const isWeatherUnspecified =
                      candidate.weatherCategory === WeatherCategory.UNSPECIFIED
                    return (
                      <TableRow
                        key={dateStr}
                        // design 実測 (`.ecl-table tbody tr:nth-child(even)` /
                        // `:hover`)。行クリックでのトグルは追加しない (見た目の
                        // 値合わせのみ、挙動は変えない — 報告参照)。
                        // 選択行は zebra と別の地 (accent の淡色) にする。 zebra と同じ
                        // 灰色だと選んだ行が縞に紛れる (UI 辞書 similar-day-search.md
                        // `tr.selected`)。 上限で選べない行は薄く (同 `.is-disabled`)。
                        background={
                          isSelected
                            ? 'Surface.accentPrimaryLightLow'
                            : idx % 2 === 1
                              ? 'Surface.secondary'
                              : undefined
                        }
                        opacity={isDisabled ? 0.45 : undefined}
                        _hover={{ background: 'Hover.lowEmphasis' }}
                        cursor={isDisabled ? 'not-allowed' : undefined}
                      >
                        <TableCell style={similarDayTdStyle}>
                          {formatDateLabel(dateStr)}
                        </TableCell>
                        <TableCell style={similarDayTdCenterStyle}>
                          <Flex
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                          >
                            <Text
                              color={
                                WEEKDAY_COLOR_TOKEN[weekdayCell.colorToken]
                              }
                              fontSize="sm"
                            >
                              {weekdayCell.label ?? '—'}
                            </Text>
                            {/* AC-04 / AC-016: 祝日は「祝」バッジと祝日名を併記する。
                              表示規則は similarDayWeekdayCell (similarDayDialog.ts
                              godoc) が決める: 名前があれば曜日区分によらず「祝」+名前、
                              名前を解決できない平日・土曜祝日は「祝」のみ、名前の無い
                              日曜祝日は出さない (spec Discussion 決定 B #3806)。
                              祝日名は en locale でも訳さない (spec 文言表)。 */}
                            {weekdayCell.badge === 'holiday' && (
                              <>
                                <Badge status="info">
                                  {t(
                                    'page:demandForecastDaily.similarDay.holiday',
                                  )}
                                </Badge>
                                {weekdayCell.holidayName !== null && (
                                  <Badge status="info">
                                    {weekdayCell.holidayName}
                                  </Badge>
                                )}
                              </>
                            )}
                          </Flex>
                        </TableCell>
                        {/* 日区分 (spec §8 / AC-DFD-014): 平日/土曜/日祝/特異日の排他4区分。
                          UNSPECIFIED は wire 契約上は非発生だが防御的に「—」表示。 */}
                        <TableCell style={similarDayTdStyle}>
                          {candidate.dayType === DayType.UNSPECIFIED ? (
                            '—'
                          ) : (
                            <Badge
                              status={dayTypeBadgeStatus(candidate.dayType)}
                            >
                              {t(
                                `page:demandForecastDaily.header.dayType.${
                                  candidate.dayType === DayType.WEEKDAY
                                    ? 'weekday'
                                    : candidate.dayType === DayType.SATURDAY
                                      ? 'saturday'
                                      : candidate.dayType === DayType.HOLIDAY
                                        ? 'holiday'
                                        : 'singular'
                                }`,
                              )}
                            </Badge>
                          )}
                        </TableCell>
                        {/* AC-01/AC-02: UNSPECIFIED (実績なし) は「—」、それ以外は
                          絵文字 + tooltip (概況ラベル)。晴の昼夜切替はしない
                          (TODO(#3805): weatherCategoryEmojiOf 参照)。 */}
                        {/* 概況ラベルは DS Tooltip で出す (UI 辞書 tooltip.md)。
                            native title は Tooltip 非対応環境向けの保険として残す
                            (HintTooltip の JSDoc)。 */}
                        <HintTooltip
                          content={weatherCategoryLabelOf(
                            candidate.weatherCategory,
                          )}
                        >
                          <TableCell
                            style={similarDayTdCenterStyle}
                            title={weatherCategoryLabelOf(
                              candidate.weatherCategory,
                            )}
                          >
                            {isWeatherUnspecified
                              ? weatherCategoryLabelOf(
                                  candidate.weatherCategory,
                                )
                              : weatherCategoryEmojiOf(
                                  candidate.weatherCategory,
                                )}
                          </TableCell>
                        </HintTooltip>
                        {/* 気温（最高/最低）1列に統合 (design L1030 テーブル版準拠, 8列→6列)。
                          AC-014: 小数1桁で表示。数値列は design `.ecl-table td.num`
                          (右寄せ + tabular-nums) */}
                        <TableCell
                          style={
                            candidate.maxTemp !== undefined ||
                            candidate.minTemp !== undefined
                              ? similarDayTdNumStyle
                              : similarDayTdNoObsStyle
                          }
                        >
                          {candidate.maxTemp !== undefined ||
                          candidate.minTemp !== undefined
                            ? `${candidate.maxTemp !== undefined ? parseFloat(candidate.maxTemp.value).toFixed(1) : '—'}℃ / ${candidate.minTemp !== undefined ? parseFloat(candidate.minTemp.value).toFixed(1) : '—'}℃`
                            : '—'}
                        </TableCell>
                        {/* 露点 (F-3, spec §機能詳細「露点 ℃」): コマ値の日平均を
                          小数1桁で表示。単日欠測時は「—」(AC-DFD-017a)。 */}
                        <TableCell
                          style={
                            candidate.dewpointCelsius !== undefined
                              ? similarDayTdNumStyle
                              : similarDayTdNoObsStyle
                          }
                        >
                          {candidate.dewpointCelsius !== undefined
                            ? `${parseFloat(candidate.dewpointCelsius.value).toFixed(1)}℃`
                            : '—'}
                        </TableCell>
                        {/* 需要（最大/最小）MW 1列に統合。AC-014: 千区切りで表示 */}
                        <TableCell style={similarDayTdNumStyle}>
                          {candidate.demandMaxMw !== undefined ||
                          candidate.demandMinMw !== undefined
                            ? `${candidate.demandMaxMw !== undefined ? parseFloat(candidate.demandMaxMw.value).toLocaleString('ja-JP', { maximumFractionDigits: 0 }) : '—'} / ${candidate.demandMinMw !== undefined ? parseFloat(candidate.demandMinMw.value).toLocaleString('ja-JP', { maximumFractionDigits: 0 }) : '—'} MW`
                            : '—'}
                        </TableCell>
                        <TableCell style={similarDayTdCheckStyle}>
                          <Checkbox
                            aria-label={t(
                              'page:demandForecastDaily.similarDay.selectDayAriaLabel',
                              {
                                date: formatDateLabel(dateStr),
                              },
                            )}
                            checked={isSelected}
                            disabled={isDisabled}
                            onCheckedChange={() => {
                              handleToggle(dateStr)
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </TableRoot>
            </Box>
          ) : (
            /* 空状態パネル。 構成・値は `design/ui-feasibility/components/
             empty-panel.md` に従う (= DS `PageError` が正)。 共通の [[EmptyPanel]]
             を使う (同じ値を手で組み直さない)。

             **Alert ではなくパネルを使う**理由も同 doc の分岐表どおり: 空の理由が
             複数あり (遡る日数の範囲に無い / 対象日の予想気温が揃っていない)、
             理由ごとに次の行動が変わる (窓を広げる / 日付を手で足す) ため。

             glyph は**虫眼鏡** — 同 doc の glyph 表で「絞り込みの結果が 0 件」に
             割り当てられているもの。「そもそも無い」 (受信箱) とは描き分ける。
             色は状態によらず `Object.disabled` 固定で、状態は glyph と文言が担う。 */
            <EmptyPanel
              action={
                <TertiaryButton onClick={openManualDateField}>
                  {t('page:demandForecastDaily.similarDay.addDateLabel')}
                </TertiaryButton>
              }
              description={t(
                'page:demandForecastDaily.similarDay.noCandidatesHint',
              )}
              icon={
                <FaMagnifyingGlassRegular
                  boxSize={iconSize.emptyPanel}
                  aria-hidden="true"
                />
              }
              title={t('page:demandForecastDaily.similarDay.noCandidatesTitle')}
            />
          )}
        </RegionLoading>

        {/* 候補はあるが 1 件も選んでいない時。 「適用」 が押せない理由がこれ
            (`isSaveDisabled={selectedDates.length === 0}`)。 この文言は元々
            この用途のキーだが、 候補 0 件の一覧に誤って出ていて**本来の位置には
            出ていなかった** (2026-08-31 デザイナー指摘の調査で判明)。 */}
        {candidates.length > 0 && selectedDates.length === 0 && (
          <Text color="Text.midEmphasis" fontSize="sm">
            {t('page:demandForecastDaily.similarDay.noApplyHint')}
          </Text>
        )}
      </Flex>
    </Modal>
  )
}
